import { Col, Collapse, Row } from 'antd';
import { useFormikContext } from 'formik';
import { getOr, isNil, keyBy } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../../../../shared/permissions';
import { useCompany } from '../../../../../../components/contexts/CompanyContextManager';
import CollapsibleWrapper from '../../../../../../components/wrappers/CollapsibleWrapper';
import FormFields from '../../../../../../themes/admin/Fields/FormFields';
import hasPermissions from '../../../../../../utilities/hasPermissions';
import {
    exportFinanceProductTableExcel,
    prepareFirstRow,
    prepareWorkbook,
} from '../../../../../../utilities/newVersionExcel';
import { useFinanceProductDetailContext } from '../../../FinanceProductDetailContext';
import ExportExcel from '../../../shared/ExportExcel';
import ImportExcel from '../../../shared/ImportExcel';
import useFinanceProductOptions from '../../../shared/useFinanceProductOptions';
import { type FinanceProductFormValues } from '../../index';
import EditableTable from '../EditableTable';
import useTerms from '../LeaseReferenceTable/useTerms';
import useAverageMileages from './useAverageMileages';
import useLeasePurchaseReferenceData from './useLeasePurchaseReferenceData';

const { Panel } = Collapse;

const colSpan = { xs: 24, sm: 8, md: 6, lg: 5, xl: 4 };

const mappedHeaders = [
    { text: 'Variant', key: 'variantName' },
    { text: 'Mileage*', key: 'mileage' },
    { text: 'Reference*', key: 'variantId' },
    { text: 'Term*', key: 'term' },
];

const excelColumnKeys = mappedHeaders.map(i => i.text);

const getVariantAndTermKey = ({ variantSuiteId, mileage, term }) => `${variantSuiteId}-${mileage}-${term}`;

const LeasePurchaseTable = ({ disabled = false }: { disabled: boolean }) => {
    const { t } = useTranslation(['financeProductDetails']);
    const { variantColumns, variantRecords, finderVehicleColumns, finderVehicleRecords } =
        useLeasePurchaseReferenceData({ isDisabled: disabled });
    const { values, setFieldValue } = useFormikContext<FinanceProductFormValues>();

    const company = useCompany(true);
    const { bank } = useFinanceProductDetailContext();

    const showFinderAssignment =
        company?.availableModules.hasFinderVehicleManagementModules &&
        hasPermissions(company?.permissions, [permissionKind.viewFinderVehicles]);

    const { amountUnitOptions } = useFinanceProductOptions(bank?.module?.companyId);
    const {
        displayName,
        term,
        residualValue: { table: leasePurchaseTable, residualValueUnit, averageMileage },
    } = values;

    const unitIsPercentage = useMemo(() => {
        if (!residualValueUnit) {
            return false;
        }
        switch (residualValueUnit) {
            case 'Percentage':
                return true;
            default:
                return false;
        }
    }, [residualValueUnit]);

    const terms = useTerms(term);
    const averageMileages = useAverageMileages(averageMileage);

    const onImport = useCallback(
        async data => {
            const currentRecords = [...variantRecords, ...finderVehicleRecords];
            const tableDataList = data
                .filter(
                    record =>
                        !isNil(record.value) &&
                        currentRecords.some(i => i.variantId === `${record.variantId}`) &&
                        terms.includes(record.term) &&
                        averageMileages.includes(record.mileage)
                )
                .map(value => ({
                    term: value.term,
                    mileage: value.mileage,
                    variantSuiteId: currentRecords.find(i => i.variantId === `${value.variantId}`).variantSuiteId,
                    value: value.value,
                }));
            setFieldValue('residualValue.table', tableDataList);
        },
        [averageMileages, finderVehicleRecords, setFieldValue, terms, variantRecords]
    );

    const exportExcel = useCallback(async () => {
        const workbook = await prepareWorkbook();
        const worksheet = workbook.getWorksheet(1);

        prepareFirstRow(worksheet, excelColumnKeys, terms);

        const leaseTableDataByVariantAndTerm = keyBy(getVariantAndTermKey, leasePurchaseTable || []);

        [...variantRecords, ...finderVehicleRecords].forEach(row => {
            const { variantSuiteId, variantName, variantId, mileage } = row;
            const values = terms.map(term => {
                const variantAndTermKey = getVariantAndTermKey({ variantSuiteId, mileage, term });

                return getOr(null, `${variantAndTermKey}.value`, leaseTableDataByVariantAndTerm);
            });
            worksheet.addRow([variantName, mileage, variantId, ...values]);
        });

        exportFinanceProductTableExcel(workbook, displayName, 'Residual Value');
    }, [leasePurchaseTable, variantRecords, finderVehicleRecords, displayName, terms]);

    return (
        <CollapsibleWrapper defaultActiveKey={['residualValue']}>
            <Panel key="residualValue" header={t('financeProductDetails:tables.leasePurchase.title')}>
                <Row gutter={[16, 16]}>
                    <Col {...colSpan}>
                        <FormFields.SelectField
                            {...t('financeProductDetails:fields.residualValueUnit', { returnObjects: true })}
                            disabled={disabled}
                            name="residualValue.residualValueUnit"
                            options={amountUnitOptions}
                            required
                            showSearch
                        />
                    </Col>
                </Row>
                <EditableTable columns={variantColumns} records={variantRecords} unitIsPercentage={unitIsPercentage} />
                {showFinderAssignment && (
                    <EditableTable
                        columns={finderVehicleColumns}
                        records={finderVehicleRecords}
                        unitIsPercentage={unitIsPercentage}
                    />
                )}
            </Panel>
            {!disabled && (
                <>
                    <ImportExcel
                        mappedHeaders={mappedHeaders}
                        onImport={onImport}
                        tableType="matrix"
                        title={t('financeProductDetails:importModal.titles.leasePurchase')}
                    />
                    <ExportExcel onClick={exportExcel} />
                </>
            )}
        </CollapsibleWrapper>
    );
};

export default LeasePurchaseTable;
